import type { UseConnectivityReturn } from '@/app/pwa/types';
import { useToasts } from '@/common/composables/useToast';
import { computed, ref } from 'vue';

// Shared state (singleton pattern)
const isOnline = ref(navigator.onLine);
const connectionType = ref<string | null>(null);
let isInitialized = false;

/**
 * @author Omar Sánchez Martínez
 * @since 2025-11-19
 * @updated 2025-11-20 - Converted to singleton pattern to prevent duplicate toast notifications
 * @description Composable for detecting and reacting to online/offline state changes
 * Provides reactive state for connectivity status and integrates with vue-sonner for notifications
 *
 * Uses a singleton pattern to ensure event listeners are only registered once globally,
 * preventing duplicate toast notifications when used in multiple components.
 *
 * @returns {UseConnectivityReturn} Object containing isOnline, isOffline, and connectionType refs
 *
 * @example
 * const { isOnline, isOffline } = useConnectivity();
 *
 * watch(isOffline, (offline) => {
 *   if (offline) {
 *     console.log('App is now offline');
 *   }
 * });
 */
export function useConnectivity(): UseConnectivityReturn {
  // Initialize event listeners only once
  if (!isInitialized) {
    const { infoToast, successToast } = useToasts();

    /**
     * Handle online event
     * Called when network connection is restored
     */
    const handleOnline = () => {
      isOnline.value = true;
      successToast('Conexión restablecida', 'Tu conexión a internet ha sido restaurada');
    };

    /**
     * Handle offline event
     * Called when network connection is lost
     */
    const handleOffline = () => {
      isOnline.value = false;
      infoToast('Sin conexión', 'No hay conexión a internet disponible');
    };

    // Register event listeners globally (only once)
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    isInitialized = true;
  }

  // Computed property for offline state
  const isOffline = computed(() => !isOnline.value);

  return {
    isOnline,
    isOffline,
    connectionType,
  };
}
